import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Truck, Mail } from "lucide-react";
import { PeptigenLogo } from "./Logo";
import Newsletter from "./Newsletter";

export default function Footer() {
  return (
    <footer
      className="border-t mt-16"
      style={{ borderColor: "rgba(0,194,214,0.18)", backgroundColor: "#080F1B" }}
      data-testid="site-footer"
    >
      <div
        className="border-y"
        style={{ borderColor: "rgba(166,255,0,0.18)", backgroundColor: "rgba(166,255,0,0.03)" }}
        data-testid="authenticity-strip"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#A6FF00]" />
            <span className="pg-mono text-[10.5px] uppercase tracking-[0.22em] text-[#F7FAFC]">
              Authenticity Notice
            </span>
          </div>
          <p className="text-[11px] text-[#9CA8B8] leading-relaxed max-w-3xl">
            Genuine Peptigen Labs products are <span className="text-[#F7FAFC]">sold direct at peptigenlabs.co.uk only</span>. We do not authorise resellers, marketplaces or third-party sellers to hold stock or sell on our behalf. Every Order is fulfilled direct by Peptigen Labs Ltd. Vial / packaging may differ from product images shown.{" "}
            <span className="text-[#F7FAFC]">Peptigen Labs Ltd is an independent UK company with no connection to any similarly named business, brand or website</span> — reviews or conduct of other companies do not relate to us.
          </p>
        </div>
      </div>

      <Newsletter />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <PeptigenLogo size={42} />
            <p className="mt-5 text-[#9CA8B8] text-sm leading-relaxed max-w-md">
              Peptigen Labs — precision research supply for UK laboratory work.
              British supplier, batch-documented, independently purity tested, dispatched from
              UK stock with next-day delivery on eligible orders.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Trust icon={ShieldCheck} label="Purity Tested" />
              <Trust icon={Lock} label="Secure Encrypted Checkout" />
              <Trust icon={Truck} label="Tracked UK Delivery" />
            </div>

            <div className="mt-6 flex flex-col gap-2 text-sm" data-testid="footer-contact">
              <a
                href="mailto:info@peptigenlabs.co.uk"
                className="inline-flex items-center gap-2 text-[#9CA8B8] hover:text-[#00C2D6] transition-colors"
                data-testid="footer-email"
              >
                <Mail size={14} className="text-[#5EEAD4]" />
                info@peptigenlabs.co.uk
              </a>
              <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8]/70 ml-6">
                Mon – Fri · 9 am – 5 pm GMT
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="pg-micro mb-4">Catalogue</div>
            <ul className="space-y-2 text-sm text-[#9CA8B8]">
              <li><Link to="/products" className="hover:text-[#00C2D6]">All Products</Link></li>
              <li><Link to="/currently-available" className="hover:text-[#00C2D6]">In Stock Now</Link></li>
              <li><Link to="/quality" className="hover:text-[#00C2D6]">Quality Standards</Link></li>
              <li><Link to="/same-day-dispatch" className="hover:text-[#00C2D6]">UK Dispatch</Link></li>
              <li><Link to="/faq" className="hover:text-[#00C2D6]" data-testid="footer-link-faq">FAQ</Link></li>
              <li><Link to="/guides/where-to-buy-peptides-uk" className="hover:text-[#00C2D6]" data-testid="footer-link-guide-buy">UK Buyer's Guide</Link></li>
              <li><Link to="/guides/how-to-read-a-peptide-coa" className="hover:text-[#00C2D6]" data-testid="footer-link-guide-coa">How to Read a COA</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="pg-micro mb-4">Company</div>
            <ul className="space-y-2 text-sm text-[#9CA8B8]">
              <li><Link to="/about" className="hover:text-[#00C2D6]">About</Link></li>
              <li><Link to="/contact" className="hover:text-[#00C2D6]">Contact</Link></li>
              <li><Link to="/legal/disclaimer" className="hover:text-[#00C2D6]">Disclaimer</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="pg-micro mb-4">Legal & Policies</div>
            <ul className="space-y-2 text-sm text-[#9CA8B8]">
              <li><Link to="/legal/terms" className="hover:text-[#00C2D6]">Terms & Conditions</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-[#00C2D6]">Privacy Policy</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-[#00C2D6]">Cookie Policy</Link></li>
              <li><Link to="/legal/shipping" className="hover:text-[#00C2D6]">Shipping Policy</Link></li>
              <li><Link to="/legal/returns" className="hover:text-[#00C2D6]">Returns & Refunds</Link></li>
              <li><Link to="/legal/research-use" className="hover:text-[#00C2D6]">Research-Use Statement</Link></li>
            </ul>
          </div>
        </div>

        <div className="pg-divider mb-8" />

        <div className="flex flex-col md:flex-row justify-between gap-6 text-xs text-[#9CA8B8]">
          <div className="max-w-3xl leading-relaxed space-y-2">
            <p>
              All products supplied by Peptigen Labs Ltd are research-use-only laboratory materials.
              Not for human or animal consumption. Not intended to diagnose, treat, cure, or
              prevent any disease. Customers must be 18+ and qualified researchers, lab
              technicians, or scientific buyers.
            </p>
            <p>
              Peptigen Labs Ltd is a company registered in England &amp; Wales
              (company number <span className="text-[#F7FAFC]">17193157</span>),
              trading as Peptigen Labs.
            </p>
            <p className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8]">
              Registered office: 50 Princes Street, Ipswich, Suffolk, IP1 1RJ
            </p>
            <p>
              Use of this website is subject to our{" "}
              <Link to="/legal/terms" className="text-[#00C2D6] hover:underline">Terms &amp; Conditions</Link>,{" "}
              <Link to="/legal/privacy" className="text-[#00C2D6] hover:underline">Privacy Policy</Link>{" "}and{" "}
              <Link to="/legal/research-use" className="text-[#00C2D6] hover:underline">Research-Use Statement</Link>.
            </p>
          </div>
          <div className="flex flex-col md:items-end gap-1">
            <span className="pg-mono uppercase tracking-[0.22em] text-[#00C2D6]">peptigenlabs.co.uk</span>
            <span>© {new Date().getFullYear()} Peptigen Labs Ltd · United Kingdom</span>
            <span className="pg-mono text-[10px] uppercase tracking-[0.22em]">Compliant with UK GDPR</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function Trust({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00C2D6]/25 rounded-sm">
      <Icon size={12} className="text-[#5EEAD4]" />
      <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8]">{label}</span>
    </div>
  );
}
